import { z } from 'zod';

import { AppliedDiscountSchema } from './discount.js';

const CURRENCY = z.string().length(3).regex(/^[A-Z]{3}$/);
const MONEY = z.string().regex(/^-?\d+(\.\d{1,4})?$/);
const QTY = z.string().regex(/^\d+(\.\d{1,4})?$/);

export const PriceListAdjustmentTypeSchema = z.enum(['none', 'percent']);
export type PriceListAdjustmentType = z.infer<typeof PriceListAdjustmentTypeSchema>;

/** A line in a price list: the unit price for an item at/above a quantity break,
 *  optionally in a specific currency. */
export const PriceListItemSchema = z.object({
  id: z.string().uuid(),
  priceListId: z.string().uuid(),
  itemId: z.string().uuid(),
  /** null = the item's BASE unit; a value references an item_units.id (pack/carton). */
  itemUnitId: z.string().uuid().nullable().optional(),
  itemName: z.string().nullable(),
  itemSku: z.string().nullable(),
  currencyCode: CURRENCY.nullable(),
  minQty: z.string(),
  unitPrice: z.string(),
});
export type PriceListItem = z.infer<typeof PriceListItemSchema>;

export const PriceListSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(1).max(150),
  currencyCode: CURRENCY.nullable(),
  adjustmentType: PriceListAdjustmentTypeSchema,
  adjustmentValue: z.string().nullable(),
  note: z.string().nullable(),
  isActive: z.boolean(),
  /**
   * Keep this group off the sales terminal: its customers are not synced to
   * tills and its prices are never reachable at the counter. For wholesale
   * groups that exist for the office but must not be sold at by a cashier.
   */
  hideFromPos: z.boolean(),
  /**
   * This group earns cashback on what it buys. Off for a wholesale account that
   * already has its discount in the price — earning on top is the discount twice.
   */
  hasCashback: z.boolean(),
  /**
   * This group gets the one-time welcome credit, paid when staff APPROVE the
   * customer into it (a signup asking for a type is pending until then).
   */
  hasRegistrationCashback: z.boolean(),
  /** Convenience: how many item overrides this list has. */
  itemCount: z.number().int().nonnegative().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type PriceList = z.infer<typeof PriceListSchema>;

export const PriceListCreateSchema = z.object({
  name: z.string().min(1).max(150),
  currencyCode: CURRENCY.optional(),
  adjustmentType: PriceListAdjustmentTypeSchema.default('none'),
  adjustmentValue: z.string().regex(/^-?\d+(\.\d{1,4})?$/).optional(),
  note: z.string().max(2000).optional(),
  hideFromPos: z.boolean().optional(),
  hasCashback: z.boolean().optional(),
  hasRegistrationCashback: z.boolean().optional(),
});
export type PriceListCreate = z.infer<typeof PriceListCreateSchema>;

export const PriceListUpdateSchema = z.object({
  name: z.string().min(1).max(150).optional(),
  currencyCode: CURRENCY.nullable().optional(),
  adjustmentType: PriceListAdjustmentTypeSchema.optional(),
  adjustmentValue: z.string().regex(/^-?\d+(\.\d{1,4})?$/).nullable().optional(),
  note: z.string().max(2000).nullable().optional(),
  isActive: z.boolean().optional(),
  hideFromPos: z.boolean().optional(),
  hasCashback: z.boolean().optional(),
  hasRegistrationCashback: z.boolean().optional(),
});
export type PriceListUpdate = z.infer<typeof PriceListUpdateSchema>;

/** Upsert a price for (list, item, unit, currency, min_qty). */
export const PriceListItemUpsertSchema = z.object({
  itemId: z.string().uuid(),
  /** null/undefined = the item's BASE unit; a value references an item_units.id. */
  itemUnitId: z.string().uuid().nullish(),
  currencyCode: CURRENCY.optional(),
  minQty: QTY.default('1'),
  unitPrice: MONEY,
});
export type PriceListItemUpsert = z.infer<typeof PriceListItemUpsertSchema>;

/**
 * One customer group (price list) × ONE selling unit paired with THIS item's
 * group price for that unit — powers the "price per customer group" section on
 * the item form. There is one entry per active list PER UNIT (the base unit plus
 * each of the item's packaging units). `itemUnitId` is null for the base unit or
 * an item_units.id for a pack/carton; `unitName`/`factor` label it. `unitPrice`
 * is the qty-1 group price (null = not set); `currencyCode` is the price's own
 * currency (defaults to the list's currency). Prices are never converted.
 */
export const ItemGroupPriceSchema = z.object({
  priceListId: z.string().uuid(),
  priceListName: z.string(),
  listCurrencyCode: CURRENCY.nullable(),
  /** null = the item's BASE unit; a value references an item_units.id. */
  itemUnitId: z.string().uuid().nullable(),
  /** Label for the unit (null for the base unit). */
  unitName: z.string().nullable().optional(),
  /** How many base units this unit holds (null for the base unit). */
  factor: z.string().nullable().optional(),
  currencyCode: CURRENCY.nullable(),
  unitPrice: z.string().nullable(),
});
export type ItemGroupPrice = z.infer<typeof ItemGroupPriceSchema>;

/** Set an item's per-group prices in one call (each list × unit qty-1 group price). */
export const ItemGroupPriceSetSchema = z.object({
  prices: z
    .array(
      z.object({
        priceListId: z.string().uuid(),
        /** null/undefined = the item's BASE unit; a value references an item_units.id. */
        itemUnitId: z.string().uuid().nullable().optional(),
        unitPrice: z.string().regex(/^\d+(\.\d{1,4})?$/).nullable(),
        currencyCode: CURRENCY.nullable().optional(),
      }),
    )
    .max(600),
});
export type ItemGroupPriceSet = z.infer<typeof ItemGroupPriceSetSchema>;

/** Result of resolving a price for a customer + item + qty + currency. */
export const ResolvedPriceSchema = z.object({
  unitPrice: z.string().nullable(),
  currencyCode: CURRENCY.nullable(),
  /** Where the price came from. */
  source: z.enum(['list-item', 'list-percent', 'base', 'none']),
  priceListId: z.string().uuid().nullable(),
  priceListName: z.string().nullable(),
  minQty: z.string().nullable(),
  /**
   * Set only when an automatic discount fired. `unitPrice` above is ALREADY the
   * discounted price — this carries the pre-discount price so a client can strike
   * it through, and is never what a client should charge from.
   */
  originalUnitPrice: z.string().nullable().optional(),
  appliedDiscount: AppliedDiscountSchema.nullable().optional(),
});
export type ResolvedPrice = z.infer<typeof ResolvedPriceSchema>;

/**
 * The PRICING BASIS of a party (customer) — the single answer to "what does this
 * customer pay in, and off which list?". Every surface (POS, documents, mandob,
 * storefront, offline terminal) drives its displayed AND charged currency from
 * `currencyCode` here; prices are NEVER converted, they are stored per list.
 *
 * `currencyCode` is `price_lists.currency_code` when the party is on an ACTIVE
 * list that declares one, else null ⇒ the caller falls back to the tenant base /
 * store currency (guests and ungrouped customers).
 */
export const PartyPricingBasisSchema = z.object({
  partyId: z.string().uuid().nullable(),
  priceListId: z.string().uuid().nullable(),
  priceListName: z.string().nullable(),
  /** The group's own currency; null ⇒ no group currency (use base/store). */
  currencyCode: CURRENCY.nullable(),
  adjustmentType: PriceListAdjustmentTypeSchema,
  adjustmentValue: z.string().nullable(),
});
export type PartyPricingBasis = z.infer<typeof PartyPricingBasisSchema>;

/** One (item, unit, qty) to price in a bulk request. */
export const BulkPriceLineRequestSchema = z.object({
  itemId: z.string().uuid(),
  /** null/undefined = the item's BASE unit; a value references an item_units.id. */
  itemUnitId: z.string().uuid().nullish(),
  /** Quantity for qty-break resolution (default 1). */
  qty: z.number().positive().max(1_000_000).optional(),
});
export type BulkPriceLineRequest = z.infer<typeof BulkPriceLineRequestSchema>;

/**
 * Bulk price resolution for a whole screen (POS grid, document form, catalogue).
 * Supply the party (preferred — the server reads their list) or an explicit
 * priceListId. `currency` is only a PREFERENCE used to break ties between rows;
 * the returned `currencyCode` is authoritative and may differ from it.
 */
export const BulkPriceRequestSchema = z.object({
  partyId: z.string().uuid().optional(),
  priceListId: z.string().uuid().optional(),
  currency: CURRENCY.optional(),
  lines: z.array(BulkPriceLineRequestSchema).min(1).max(500),
});
export type BulkPriceRequest = z.infer<typeof BulkPriceRequestSchema>;

/** One resolved price in a bulk response (mirrors ResolvedPrice + the echoed key). */
export const BulkPriceLineSchema = z.object({
  itemId: z.string().uuid(),
  itemUnitId: z.string().uuid().nullable(),
  qty: z.string(),
  unitPrice: z.string().nullable(),
  currencyCode: CURRENCY.nullable(),
  source: z.enum(['list-item', 'list-percent', 'base', 'none']),
  minQty: z.string().nullable(),
  /**
   * Set only when an automatic discount fired. `unitPrice` above is ALREADY the
   * discounted price — this carries the pre-discount price so a client can strike
   * it through, and is never what a client should charge from.
   */
  originalUnitPrice: z.string().nullable().optional(),
  appliedDiscount: AppliedDiscountSchema.nullable().optional(),
});
export type BulkPriceLine = z.infer<typeof BulkPriceLineSchema>;

export const BulkPriceResponseSchema = z.object({
  basis: PartyPricingBasisSchema,
  items: z.array(BulkPriceLineSchema),
});
export type BulkPriceResponse = z.infer<typeof BulkPriceResponseSchema>;

/**
 * The whole catalogue's prices in one read — every product in every selling
 * unit (the base piece, then each pack, hidden ones flagged) against the normal
 * price and every ACTIVE customer group. The items page turns it into the
 * "Export prices" spreadsheet. Each price stays in its own currency.
 */
export const ItemPriceSheetUnitSchema = z.object({
  /** null = the base piece (the item itself). */
  unitId: z.string().uuid().nullable(),
  name: z.string(),
  /** How many base pieces this unit holds (1 for the base). */
  factor: z.string(),
  /** A packaging unit switched off: kept on the product, sold nowhere. */
  hidden: z.boolean(),
  barcode: z.string().nullable(),
  /** The other codes that scan to this same unit. */
  otherBarcodes: z.array(z.string()),
  salePrice: z.string().nullable(),
  /** Only for a viewer allowed to see costs (ITEM_COST_PERMISSIONS). */
  purchasePrice: z.string().nullable().optional(),
  /**
   * What this unit cost on the newest posted purchase bill (cost viewers only):
   * the bill line's price scaled from the unit it was bought in to this one.
   */
  lastPurchasePrice: z.string().nullable().optional(),
  /** One entry per `lists[i]`, in the same order; null = not priced on that list. */
  prices: z.array(z.object({ unitPrice: z.string(), currencyCode: CURRENCY.nullable() }).nullable()),
});
export type ItemPriceSheetUnit = z.infer<typeof ItemPriceSheetUnitSchema>;

export const ItemPriceSheetItemSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  itemType: z.string(),
  isActive: z.boolean(),
  isHidden: z.boolean(),
  categoryId: z.string().uuid().nullable(),
  brandId: z.string().uuid().nullable(),
  saleCurrencyCode: CURRENCY.nullable(),
  purchaseCurrencyCode: CURRENCY.nullable().optional(),
  /** The newest posted bill's currency (cost viewers only). */
  lastPurchaseCurrencyCode: CURRENCY.nullable().optional(),
  /** Pieces on hand per `warehouses[i]`, same order (stock viewers only). */
  onHand: z.array(z.string()).optional(),
  units: z.array(ItemPriceSheetUnitSchema),
});
export type ItemPriceSheetItem = z.infer<typeof ItemPriceSheetItemSchema>;

export const ItemPriceSheetSchema = z.object({
  lists: z.array(z.object({ id: z.string().uuid(), name: z.string(), currencyCode: CURRENCY.nullable() })),
  /** Whether purchase prices are in (the viewer may see costs). */
  withCost: z.boolean(),
  /** The warehouses `onHand` is counted in — active ones the viewer may see; absent without stock.read. */
  warehouses: z.array(z.object({ id: z.string().uuid(), code: z.string(), name: z.string() })).optional(),
  items: z.array(ItemPriceSheetItemSchema),
});
export type ItemPriceSheet = z.infer<typeof ItemPriceSheetSchema>;
